import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ChevronRightIcon, InvestmentPathType, PencilIcon, RawButton, SimpleHint, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import { useHistory } from '@/modules/core/hooks';
import { useMyEnrollments } from '@/modules/transaction';

import { useInvestor } from '../hooks';
import useIsFullLiquidation from '../hooks/useIsFullLiquidation';
import { InvestmentPaths } from '../investments.types';
import EditBreakdownItem from '../InvestmentsBreakdown/EditBreakdownItem';

import InvestmentReactivationModal from './InvestmentReactivationModal';
import InvestmentSwitchModelModal from './InvestmentSwitchModelModal';

interface Props {
  accountId?: string;
  isIconMode?: boolean;
  isMobileMode?: boolean;
}

const WrapperButton = styled(Box)`
  &:hover {
    background: ${({ theme }) => theme.colors.buttonPrimary}1A;
    svg {
      fill: ${({ theme }) => theme.colors.iconAccent};
    }
  }
`;

const InvestmentSwitchModel: React.FC<Props> = ({ accountId, isIconMode, isMobileMode }) => {
  const [visible, onSetVisible] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  const { currentMyAccount } = useMyEnrollments(accountId);
  const handleOpenSwitcher = useCallback(() => {
    onSetVisible(true);
  }, []);
  const { data } = useInvestor(accountId);

  const isSelfDirectedMode = useMemo(() => data?.type === InvestmentPaths.SELF_DIRECTED, [data?.type]);
  const riskModel = useMemo(() => {
    return data?.currentToleranceModel;
  }, [data?.currentToleranceModel]);

  const isSwitchDisabled = useMemo(() => {
    return isSelfDirectedMode
      ? !currentMyAccount?.investmentOptionTypes?.includes(InvestmentPathType.MANAGED)
      : !currentMyAccount?.investmentOptionTypes?.includes(InvestmentPathType.SELF_DIRECTED);
  }, [currentMyAccount?.investmentOptionTypes, isSelfDirectedMode]);
  const { isFullLiquidated, isFullLiquidation } = useIsFullLiquidation({ accountId });
  const handleRedirectToSwitchRisk = useCallback(() => {
    history.push(
      `${ROUTES.INVESTMENTS_UPDATE_RISK_PORTFOLIO}?accountId=${accountId}&investorId=${data.investorId}`
    );
  }, [accountId, data.investorId, history]);

  if (isFullLiquidation) {
    return (
      <SimpleHint
        content={
          <Text size="small" color="textOnColor" textAlign="center">
            {t('Your full portfolio liquidation is in progress, so you cannot switch investment type.')}
          </Text>
        }
      >
        <Box
          align="center"
          justify="center"
          width="40px"
          height="40px"
          style={{ cursor: 'pointer', position: 'relative', zIndex: 1000 }}
        >
          <PencilIcon color="iconDisabled" size="20px" style={{ opacity: 0.4 }} />
        </Box>
      </SimpleHint>
    );
  }

  return (
    <>
      {isFullLiquidated ? (
        <InvestmentReactivationModal
          isSelfDirectedMode={isSelfDirectedMode}
          accountId={accountId}
          visible={visible}
          onSetVisible={onSetVisible}
        />
      ) : (
        <InvestmentSwitchModelModal
          isSelfDirectedMode={isSelfDirectedMode}
          accountId={accountId}
          riskModel={riskModel}
          onChangeRiskModel={handleRedirectToSwitchRisk}
          visible={visible}
          onSetVisible={onSetVisible}
        />
      )}
      {isMobileMode ? (
        <RawButton data-testid="switch-model__button" onClick={handleOpenSwitcher}>
          <ChevronRightIcon color="iconDisabled" size="20px" />
        </RawButton>
      ) : isIconMode ? (
        <RawButton data-testid="switch-model__button" onClick={handleOpenSwitcher} style={{ zIndex: 9999 }}>
          <WrapperButton round="50%" width="40px" height="40px" align="center" justify="center">
            <PencilIcon color="iconDisabled" size="20px" />
          </WrapperButton>
        </RawButton>
      ) : (
        <EditBreakdownItem
          testId="switch-model"
          onClick={handleOpenSwitcher}
          label={t('Investment model')}
          disabled={isSwitchDisabled}
        >
          {isSelfDirectedMode ? t('Self-Directed') : t('Managed')}
        </EditBreakdownItem>
      )}
    </>
  );
};
export default InvestmentSwitchModel;
